import type { ChannelId } from '../../outreach/components/channel-switcher'
import type { HomeStateKey } from './copy'

/**
 * What a card's buttons do.
 *
 * `route` navigates, `action` runs something in place (a dialog, a mutation,
 * support). Actions rather than routes wherever the founder should not lose
 * their place: opening the connect dialog on Home is one click, sending them to
 * /outreach to find it is three.
 */
export type HomeCtaTarget =
  | { kind: 'route'; to: string; search?: Record<string, string> }
  | { kind: 'action'; action: HomeAction }

export type HomeAction =
  | 'upload-deck'
  | 'add-funds'
  | 'resume-sending'
  | 'pause-sending'
  | 'approve-all'
  | 'approve-contacts'
  | 'support'
  | 'copy-share-link'
  | 'escape-to-share'

export interface HomeCtaConfig {
  primary?: HomeCtaTarget
  ghost?: HomeCtaTarget
}

const route = (to: string, search?: Record<string, string>): HomeCtaTarget => ({
  kind: 'route',
  to,
  search,
})

const action = (a: HomeAction): HomeCtaTarget => ({ kind: 'action', action: a })

/**
 * Which campaign board to open.
 *
 * The tab in this table names the usual case, not the channel the founder is
 * necessarily running. /outreach opens the investor board by default, so a
 * founder running warm intros only was sent to an empty board holding none of
 * the contacts or messages the card had just asked them to deal with.
 *
 * Two questions, in order. A card that fired *about* a track — "your warm
 * intro contacts ran out", and its Approve more button — must open that track,
 * whatever else is running: sending someone to the healthy board to fix the
 * starving one is the worst version of this. Failing that, open whichever
 * campaign the founder actually runs.
 *
 * Undefined means Home has not loaded yet — keep the default rather than guess.
 */
export const outreachTabFor = (home?: {
  investorTrackEnabled?: boolean
  /** Tracks the card is about, when it is about one — Home's `drainedTracks`. */
  tracks?: readonly ('investor' | 'warm_intro')[]
}): ChannelId => {
  const named = home?.tracks?.[0]
  if (named) return named === 'warm_intro' ? 'portfolio' : 'investors'
  return home?.investorTrackEnabled === false ? 'portfolio' : 'investors'
}

/**
 * Every state's destinations, keyed off the generated union so a new state
 * cannot ship without somewhere for its buttons to go.
 *
 * Routes are relative to the startup base path; the hook prefixes them.
 *
 * Three destinations the mockup named do not exist, and are resolved here
 * rather than left to be discovered mid-implementation:
 *
 * - "See send history" → /activity, which is the send history.
 * - "How we keep your account secure" → cut. It pointed at a Help Center with
 *   no published articles, and the connect dialog already carries the security
 *   explanation, so a second button opening the same thing is noise.
 * - "Wallet pricing page" → /billing, which holds the wallet and its pricing.
 */
export const HOME_CTA: Record<HomeStateKey, HomeCtaConfig> = {
  // The dropzone is in the card itself, so the button is the file picker.
  QUEST_UPLOAD_DECK: { primary: action('upload-deck') },
  QUEST_READING_DECK: { primary: route('/profile') },
  QUEST_READ_FAILED: { primary: action('support'), ghost: route('/profile') },
  QUEST_COMPLETE_PROFILE: { primary: route('/profile'), ghost: route('/pitch') },
  // The dropzone-equivalent visual carries the URL + copy button itself, so the
  // primary button is the same copy action, not a route.
  QUEST_SHARE: { primary: action('copy-share-link'), ghost: route('/profile/links') },
  QUEST_FINDING_INVESTORS: { ghost: route('/investors') },
  // Not a route: this card's whole job is to get the founder approving, so the
  // action starts the campaign and opens the approve dialog rather than landing
  // them on a list to find it themselves.
  // Both send the founder to the outreach page, where the real controls live.
  // Ghost is the escape back to self-share — see escape-to-share.
  QUEST_REVIEW_MESSAGES: { primary: action('approve-contacts'), ghost: action('escape-to-share') },
  // No tab here or anywhere below: every '/outreach' route gets the founder's
  // own campaign tab applied by the runner. See outreachTabFor.
  QUEST_APPROVE_OUTREACH: { primary: route('/outreach') },
  // Connecting a sender is a multi-step flow the outreach page owns end to end
  // (invite, sign-in link, sender poll). Home sends the founder there rather
  // than opening a second copy of that dialog on top of the wizard.
  // Ghost is the escape back to self-share.
  QUEST_CONNECT_LINKEDIN: { primary: route('/outreach'), ghost: action('escape-to-share') },
  // No escape here: the founder already committed to a workspace being
  // provisioned, and the ghost slot is spoken for by support during the wait.
  QUEST_PREPARING_LINK: { ghost: action('support') },
  // Launching is the outreach page's own confirmation flow. Ghost is the
  // escape back to self-share.
  QUEST_LAUNCH_CAMPAIGN: { primary: route('/outreach'), ghost: action('escape-to-share') },
  QUEST_LIVE: { primary: route('/pitch/start'), ghost: route('/outreach') },
  QUEST_PAUSED: { primary: action('resume-sending'), ghost: route('/outreach/replies') },
  QUEST_RECONNECT: { primary: route('/outreach') },
  // `focus=unread` lands the founder on the reply the card is about rather
  // than on the stat cards above it.
  MOMENT_REPLY: { primary: route('/outreach/replies', { focus: 'unread' }) },
  MOMENT_OUTCOME_VERIFYING: { primary: route('/outreach/replies', { focus: 'outcome' }) },
  MOMENT_OUTCOME_CONFIRMED: {
    primary: route('/outreach/replies', { focus: 'outcome' }),
    // "Question this charge" is the support path, deliberately not a dispute
    // form: a human reads it and the founder is not made to argue with a UI.
    ghost: action('support'),
  },
  ALERT_RECONNECT: { primary: route('/outreach'), ghost: action('support') },
  ALERT_SYSTEM_STALL: { primary: action('support'), ghost: route('/activity') },
  ALERT_ADD_FUNDS: { primary: action('add-funds'), ghost: action('pause-sending') },
  ALERT_QUEUE_DRAINED: { primary: route('/outreach'), ghost: action('approve-all') },
}

/** Where a banner's inline link goes. */
export const BANNER_CTA: Record<string, HomeCtaTarget> = {
  BANNER_TRIAL_ENDING: action('add-funds'),
  // The campaign board, not the match list: approving happens on the board,
  // and /investors is where you browse who was matched.
  BANNER_LOW_RUNWAY: route('/outreach'),
  BANNER_REPLIES_WAITING: route('/outreach/replies', { focus: 'unread' }),
}

/** The `pill` link in the money-disclosure line. */
export const PILL_TARGET: HomeCtaTarget = route('/billing')
